import { useContext } from "react";
import { ROUTE_PARAMS } from "@/constants";
import ErrorPageIllustration from "@/components/ErrorPageIllustration";
import { MeContext } from "@/context/me/MeContext";
import { Grid, Stack } from "@mui/material";
import { Button, Typography } from "@trimble/modus-cmx";
import { isDealerWithoutRole } from "@/permissions";
import { authSignOutRedirect } from "@/provider/AuthProvider/signOut";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useNavigate, useParams } from "react-router";

type AccessDeniedProps = {
  variant?: "meForbidden" | "default";
};

export default function AccessDenied(props: AccessDeniedProps) {
  const { variant = "default" } = props;
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const { [ROUTE_PARAMS.ACCOUNT_ID]: accountId } = useParams();
  const meContext = useContext(MeContext);

  const dashboardPath = accountId ? `/${accountId}` : "/";
  const dealerWithoutRole =
    meContext &&
    isDealerWithoutRole(meContext.me.role, meContext.me.permissionList);
  const shouldHideDashboardButton =
    variant === "meForbidden" || dealerWithoutRole;
  const titleKey = dealerWithoutRole
    ? "access_denied.account_title"
    : "access_denied.title";

  return (
    <Grid container size="grow" spacing={2} alignItems="center">
      <Grid size={{ xs: 12, md: 6 }}>
        <Stack sx={{ gap: "30px" }}>
          <Stack gap={2}>
            <Typography hierarchy="h2" size="2xl" weight="bold">
              {t("access_denied.heading")}
            </Typography>
            <Typography hierarchy="p" size="lg">
              {t(titleKey)}
            </Typography>
          </Stack>
          <Stack direction="row" gap={3} flexWrap="wrap">
            {!shouldHideDashboardButton && (
              <Button onButtonClick={() => navigate(dashboardPath)}>
                {t("access_denied.dxp_dashboard")}
              </Button>
            )}
            <Button
              variant="outlined"
              color="neutral"
              onButtonClick={() => {
                void authSignOutRedirect(auth.signoutRedirect);
              }}
            >
              {t("profile_drawer.sign_out")}
            </Button>
          </Stack>
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <ErrorPageIllustration alt={t("access_denied.heading")} />
      </Grid>
    </Grid>
  );
}
